import { useMutation } from '@apollo/client/react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CustomButton } from '../../../components/customButton/CustomButton';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { useGetColumns } from '../hooks/useGetColumns';
import { useGetFilters } from '../hooks/useGetFilters';
import { useGetRows } from '../hooks/useGetRows';
import { BorrarConexionesFilters } from './BorrarConexionesFilters';
import { formatCurrency } from '../../../utils/mathUtils';
import { BORRAR_CONEXIONES } from '../../../data/graphQL/mutations/BorrarConexiones/BorrarConexiones';
import { useLocation } from 'react-router-dom';

const mensajeAdvertencia = `Antes de borrar conexiones debera preguntar a facturación si no es fecha de cierre. Si es fecha de cierre no borre conexiones.
Recuerde que el borrado de las conexiones no se puede revertir y la conexión no desaparece si no cambia de estado.`;

export const BorrarConexionesContent = ({ setChildrenAction, setHeaderContent }) => {
    const rutasStorage = localStorage.getItem('navigationRoutes');
    const location = useLocation();

    const [allowedWrite, setAllowedWrite] = useState(false);
    const [isRefetchRequired, setIsRefetchRequired] = useState(true);

    const {
        filters,
        handleChangeFilters,
        clearFilters
    } = useGetFilters();

    const {
        rows,
        setRows,
        loading,
        totalCount,
        handleSearch
    } = useGetRows(filters, setIsRefetchRequired);

    const columns = useGetColumns();

    const [borrarConexiones, { loading: loadingBorrar }] = useMutation(BORRAR_CONEXIONES);

    const handleFilters = (name, value) => {
        handleChangeFilters(name, value);
        setRows([]);
        setIsRefetchRequired(true);
    }

    const handleBorrar = async () => {
        const idConexiones = rows.map(row => row.idConexion);

        await borrarConexiones({
            variables: {
                input: idConexiones
            }
        }).then((res) => {
            if (res?.data?.borrarConexiones) {
                toast.success('Conexiones borradas correctamente');
                handleSearch();
            }
            else {
                toast.error('Error al borrar las conexiones. Intenta nuevamente.');
            }
        }).catch((err) => {
            toast.error('Error al borrar las conexiones. Intenta nuevamente.');
        });
    }

    const importeTotal = useMemo(() => {
        return rows.reduce((acc, row) => acc + row.precioDiario, 0);
    }, [rows]);

    useEffect(() => {
        if (rutasStorage) {
            const rutas = JSON.parse(rutasStorage);
            const rutaQueSirve = location.pathname;

            setAllowedWrite(rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
            )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura);

        }
    }, [rutasStorage]);

    useEffect(() => {
        const getTooltipText = () => {
            if (!allowedWrite) {
                return 'Usted no tiene permisos para borrar conexiones';
            }
            if (rows.length === 0) {
                return 'No hay conexiones para borrar con los filtros seleccionados';
            }
            return 'Borrar conexiones';
        };

        setChildrenAction(() => (
            <Tooltip title={getTooltipText()}>
                <span>
                    <CustomButton
                        onClick={() => handleBorrar()}
                        variant='contained'
                        isLoading={loadingBorrar}
                        disabled={rows.length === 0 || !allowedWrite}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Borrar conexiones
                    </CustomButton>
                </span>
            </Tooltip>
        ));
    }, [
        setChildrenAction,
        loadingBorrar,
        rows,
        allowedWrite
    ]);

    useEffect(() => {
        setHeaderContent(
            <Typography
                sx={{
                    my: 2,
                    color: 'error.main',
                    whiteSpace: 'pre-wrap',
                    width: '75%',
                }}
            >
                {mensajeAdvertencia}
            </Typography>
        );
    }, [setHeaderContent]);

    return (
        <div>
            <BorrarConexionesFilters
                filters={filters}
                handleChangeFilters={handleFilters}
                handleSearch={handleSearch}
                disableFilters={loading || loadingBorrar}
                clearFilters={clearFilters}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        backgroundColor: "#E0E1E3",
                        borderRadius: "10px",
                        px: 2,
                        py: 1.25,
                    }}
                >
                    <Typography variant="body2">
                        <strong>Cantidad de conexiones a borrar:{" "}</strong>
                        {isRefetchRequired ? '-' : totalCount}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Importe total a dar de baja:{" "}</strong>
                        {isRefetchRequired ? '-' : formatCurrency(importeTotal)}
                    </Typography>
                </Box>
            </Box>
            <DataGridGenerico
                entityId='idConexion'
                rows={rows}
                columns={columns}
                totalCount={totalCount}
                loading={loading}
                toolbar={false}
                localeText={{
                    noRowsLabel: isRefetchRequired ?
                        'Haz click en "Buscar" para aplicar los nuevos filtros' :
                        "Sin filas"
                }}
            />
        </div>
    );
}